import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api from "@/lib/api";
import { NotFound } from "@/pages/ErrorPages";
import { buildAutoplayMutedSrc } from "@/lib/embeds";
import { Play, Share2, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function ReelShare() {
  const { reelSlug } = useParams();
  const { slug, sp } = useStore();
  const [data, setData] = useState(null);
  const [pageStatus, setPageStatus] = useState("loading");

  useEffect(() => {
    api.get(`/public/tenants/${slug}/reels/${reelSlug}`)
      .then((r) => { setData(r.data); setPageStatus("ready"); })
      .catch(() => setPageStatus("not_found"));
  }, [slug, reelSlug]);

  if (pageStatus === "loading") return null;
  if (pageStatus === "not_found" || !data) return <NotFound />;

  const tenant = data.tenant;
  const reel = data.reel;
  const themeColor = tenant.theme_color;

  const onShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Share link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="reel-share-page">
      {/* Minimal header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-[1280px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={sp(``)} className="flex items-center gap-3" data-testid="reel-share-brand">
            <div className="h-7 w-7 grid place-items-center text-[10px] font-bold tracking-widest" style={{ backgroundColor: themeColor }}>
              {tenant.name.slice(0, 2).toUpperCase()}
            </div>
            <span className="font-display font-bold tracking-tight">{tenant.name}</span>
          </Link>
          <button
            onClick={onShare}
            className="px-4 py-2 text-[11px] uppercase tracking-[0.25em] border border-white/15 hover:border-white/40 inline-flex items-center gap-2"
            data-testid="reel-share-button"
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-10">
        <div className="relative w-full max-w-[420px] aspect-[9/16] bg-black overflow-hidden border border-white/10" style={{ boxShadow: `0 0 80px -10px ${themeColor}55` }}>
          <iframe
            src={buildAutoplayMutedSrc(reel.video_source)}
            title={reel.title}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
            data-testid="reel-share-iframe"
          />
        </div>

        <div>
          <span className="inline-flex items-center gap-2 px-3 py-1 border text-[10px] uppercase tracking-[0.3em]" style={{ borderColor: `${themeColor}55`, color: themeColor }}>
            <Sparkles className="w-3 h-3" /> Reel
          </span>
          <h1 className="mt-5 font-display font-black tracking-tighter text-4xl md:text-5xl leading-[0.95]" data-testid="reel-share-title">
            {reel.title}
          </h1>
          <div className="mt-3 text-[11px] uppercase tracking-[0.25em] text-white/50">
            {reel.creator_name || tenant.name}
          </div>
          {reel.description && <p className="mt-5 text-white/65 font-light leading-relaxed max-w-xl">{reel.description}</p>}

          {reel.linked_content && (
            <Link
              to={sp(`/title/${reel.linked_content.slug}`)}
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.25em] transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: themeColor, color: "#0a0a0a" }}
              data-testid="reel-share-watch-full"
            >
              <Play className="w-4 h-4 fill-black" /> Watch full film: {reel.linked_content.title}
            </Link>
          )}

          <div className="mt-12 flex flex-wrap items-center gap-3">
            <Link
              to={sp(`/reels`)}
              className="px-5 py-3 text-xs uppercase tracking-[0.25em] border border-white/15 hover:border-white/40 inline-flex items-center gap-2"
              data-testid="reel-share-feed-link"
            >
              See more reels <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={sp(``)}
              className="px-5 py-3 text-xs uppercase tracking-[0.25em] text-white/70 hover:text-white"
              data-testid="reel-share-studio-link"
            >
              Browse {tenant.name}
            </Link>
          </div>

          {!tenant.white_label_enabled && (
            <div className="mt-12 text-[10px] uppercase tracking-[0.3em] text-white/30">
              Powered by Vawworld
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
